import FadeInPanel from '@/shared/Animated/FadeInPanel'
import usePopup from '@/shared/hooks/usePopup'
import { IOption } from '@/types/global'
import cn from '@/utils/cn'
import { CSSProperties, useMemo, useRef, useState } from 'react'
import css from './dropdownSearchFilterChips.module.css'
import ArrowDown from '../../../../assets/icons/arrowDown.svg?react'
import Close from '../../../../assets/icons/close.svg?react'
import SearchInput from '../../SearchInput'
import Button from '@/shared/Buttons/Button'
import { Virtuoso } from 'react-virtuoso'
import Skeleton from '@/shared/Loaders/Skeleton'
import DropdownInputListItem from '@/shared/Dropdowns/DropdownInputListItem'

interface IProps<T extends string | number> {
  options: IOption<T>[]
  onSelect: (options: T[], name: string) => void
  selectedOptions: T[]

  panelStyle?: CSSProperties
  title?: string
  disabled?: boolean
  style?: CSSProperties
  name?: string
  loading?: boolean
  searchPlaceholder?: string
}

const DropdownSearchFilterChips = <T extends string | number>(props: IProps<T>) => {
  const [query, setQuery] = useState('')
  const [tempSelectedOptions, setTempSelectedOptions] = useState<Set<T>>(new Set(props.selectedOptions))
  const ref = useRef<HTMLInputElement>()
  const { open, setOpen } = usePopup(ref)
  const virtuosoRef = useRef(null)

  const handleSelect = (option: IOption<T>) => {
    setTempSelectedOptions((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(option.value)) {
        newSet.delete(option.value)
      } else {
        newSet.add(option.value)
      }
      return newSet
    })
  }

  const preparedOptions = useMemo(() => {
    const filteredOptions = query.trim()
      ? props.options.filter(
          (option) =>
            (option.title as string).toLowerCase().includes(query.toLowerCase()) ||
            props.selectedOptions.includes(option.value)
        )
      : props.options

    const sortedOptions = filteredOptions.sort((a, b) => {
      const aSelected = props.selectedOptions.includes(a.value)
      const bSelected = props.selectedOptions.includes(b.value)

      if (aSelected && !bSelected) return -1
      if (!aSelected && bSelected) return 1

      if (typeof a.title === 'string' && typeof b.title === 'string') return a.title.localeCompare(b.title)
      return 0
    })
    return sortedOptions
  }, [props.options, query, open, props.selectedOptions])

  const renderOption = (option: IOption<T>, index: number) => {
    return (
      <div style={{ padding: '0 4px' }}>
        <DropdownInputListItem
          key={index}
          onClick={(ev) => {
            ev.stopPropagation()
            handleSelect(option)
          }}
          active={tempSelectedOptions.has(option.value)}
        >
          <p className="text-nm">{option.title}</p>
        </DropdownInputListItem>
      </div>
    )
  }

  const renderInput = () => {
    const tip =
      props.selectedOptions.length === 0 || props.selectedOptions.length === props.options.length
        ? 'Все'
        : `Выбрано ${props.selectedOptions.length}`
    return (
      <div>
        <span data-filled={props.selectedOptions.length > 0} className={cn('text-nm line-clamp', css.inputText)}>
          {props.title}
          <span className={cn(css.inputTextTip, 'text-heading-6')}>{tip}</span>
        </span>
      </div>
    )
  }

  const renderFooter = () => {
    if (props.loading) return null
    const isFooterDisabled = tempSelectedOptions.size === 0
    const clearButtonTip = isFooterDisabled ? '' : `(${tempSelectedOptions.size})`
    return (
      <div
        className={cn('flex-space-between', css.btnContainer)}
        onClick={(ev) => {
          ev.stopPropagation()
        }}
      >
        <Button
          size={'default'}
          type={'tetriary'}
          text={`Сбросить ${clearButtonTip}`}
          disabled={isFooterDisabled}
          onClick={() => {
            setTempSelectedOptions(new Set()), props.onSelect([], props.name)
          }}
        />
        <Button
          size={'default'}
          type={'primary'}
          disabled={isFooterDisabled}
          text="Применить"
          onClick={() => {
            props.onSelect([...tempSelectedOptions], props.name), setOpen(false)
          }}
        />
      </div>
    )
  }

  const renderSkeletonOptions = () => {
    if (!props.loading) return null
    return (
      <>
        <div className={css.skeletonContainer}>
          <Skeleton width="80%" height="15px" />
        </div>
        <div className={css.skeletonContainer}>
          <Skeleton width="60%" height="15px" />
        </div>
        <div className={css.skeletonContainer}>
          <Skeleton width="80%" height="15px" />
        </div>
      </>
    )
  }

  const renderClose = () => {
    if (!props.selectedOptions || props.selectedOptions.length === 0) return null
    return (
      <div
        className={css.closeIcon}
        onClick={(e) => {
          e.stopPropagation()
          setTempSelectedOptions(new Set()), props.onSelect([], props.name)
        }}
      >
        <Close width={8.5} height={8.5} />
      </div>
    )
  }

  return (
    <div
      className={css.wrapper}
      style={props.style}
      data-filled={props.selectedOptions.length > 0}
      data-open={open}
      ref={ref}
      onClick={!props.disabled ? () => setOpen((prev) => !prev) : null}
    >
      {renderInput()}
      {!props.disabled && (
        <ArrowDown data-open={open} data-filled={tempSelectedOptions.size > 0} className={css.arrow} />
      )}
      {renderClose()}
      <FadeInPanel open={open} className={css.options} style={props.panelStyle}>
        <div
          className={css.searchContainer}
          onClick={(ev) => {
            ev.stopPropagation()
          }}
        >
          <SearchInput
            placeHolder={props.searchPlaceholder ?? 'Поиск'}
            value={query}
            name={props.name + '_search'}
            onChange={setQuery}
          />
        </div>
        <Virtuoso
          ref={virtuosoRef}
          style={{ height: preparedOptions.length > 6 ? '256px' : `${32 * preparedOptions.length}px` }}
          totalCount={preparedOptions.length}
          itemContent={(index) => renderOption(preparedOptions[index], index)}
        />
        {renderSkeletonOptions()}
        {renderFooter()}
      </FadeInPanel>
    </div>
  )
}

export default DropdownSearchFilterChips
